/**
 * Auth Token Interceptor (Functional)
 *
 * Handles 401 Unauthorized responses by attempting a silent token
 * refresh via MSAL and retrying the failed request. This is the
 * second interceptor in the chain — the MSAL interceptor handles
 * initial token acquisition, while this handles token expiry.
 *
 * On 401:
 *   1. Acquire a new token silently via MSAL
 *   2. Retry the original request with the new token
 *   3. If refresh fails, redirect to login
 */
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { MSAL_SCOPES } from '../../config/auth.config';

/** URL patterns that should skip 401 retry handling. */
const SKIP_RETRY_PATTERNS = [
  '/authorizeuser',
  '/refreshtoken',
  '/validatetoken',
  '/login',
  '/logout',
  '/assets/',
  '.json',
  // Device-token-gated (or unauthenticated) endpoints use a bare 401 for
  // normal, expected failures (wrong badge, revoked token, invalid
  // enrollment token) — never an MSAL session concern. Redirecting to
  // /login on these would hijack the badge-login/enrollment flows, which
  // are deliberately usable without any MSAL session.
  '/badges/verify',
  '/badges/msal-verify',
  '/devices/status',
  '/devices/enroll',
];

function shouldSkipRetry(url: string): boolean {
  return SKIP_RETRY_PATTERNS.some((pattern) => url.includes(pattern));
}

function addAuthHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const msalService = inject(MsalService);
  const router = inject(Router);

  if (shouldSkipRetry(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // 401 — attempt silent token refresh
      const account = msalService.instance.getActiveAccount();
      if (!account) {
        // No account — redirect to login
        router.navigate(['/login']);
        return throwError(() => new Error('Not authenticated'));
      }

      return from(
        msalService.instance.acquireTokenSilent({
          scopes: MSAL_SCOPES,
          account,
        }),
      ).pipe(
        switchMap((response) => {
          const retryReq = addAuthHeader(req, response.accessToken);
          return next(retryReq);
        }),
        catchError((refreshError: unknown) => {
          // Refresh failed — clear account and redirect to login
          msalService.instance.setActiveAccount(null);
          router.navigate(['/login']);
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};